import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableTitle,
} from '@/components/Table/Table';
import { Typography } from '@/components/Typography';
import type { MoneyFragment } from '@/graphql/saleor/api';
import { useIsSmUp } from '@/hooks/useMedia';
import type { MergedRecommendPrice, RecommendPrice } from '@/lib/service/type';
import { serviceMessages } from '@/messages/service';
import { useRegionProvider } from '@/providers/RegionProvider';

import { handleMergePricesData } from './helper';

import * as S from './PriceTable.styles';

type PriceTableProps = {
  packShipIncluded?: boolean;
  prices: RecommendPrice[];
  vatIncluded?: boolean;
};

export const PriceTable = ({
  packShipIncluded = false,
  prices,
  vatIncluded = false,
}: PriceTableProps) => {
  const { formatMessage } = useIntl();
  const { country } = useRegionProvider();
  const isSmUp = useIsSmUp();
  const isUsMarket = country.code === 'US';
  const pricesTaxInclMessage = isUsMarket
    ? serviceMessages.pricesSalesTaxIncl
    : serviceMessages.pricesVatIncl;
  const pricesTaxNotInclMessage = isUsMarket
    ? serviceMessages.pricesSalesTaxNotIncl
    : serviceMessages.pricesVatNotIncl;
  const [mergedPrices, setMergedPrices] = useState<MergedRecommendPrice[]>([]);

  useEffect(() => {
    setMergedPrices(handleMergePricesData(prices));
  }, [prices]);

  const renderCateogory2TableCell = (categories: string[]) => {
    return (
      <TableCell>
        {categories.map((category2, index) => (
          <S.Text
            key={`${category2}-${index}`}
            scale='h6'
            textTransform='uppercase'
          >
            {category2}
          </S.Text>
        ))}
      </TableCell>
    );
  };

  const renderPriceTableCell = (prices: MoneyFragment[]) => {
    return (
      <TableCell>
        {prices.map((price, index) => (
          <S.Text
            key={`${price.currency}-${index}`}
            scale='h6'
            textAlign='center'
            textTransform='uppercase'
          >
            {price.amount === null
              ? formatMessage(serviceMessages.priceTableEstimate)
              : `${price.currency} ${price.amount}`}
          </S.Text>
        ))}
      </TableCell>
    );
  };
  return (
    <Table>
      <TableTitle title={formatMessage(serviceMessages.maximumPrices)}>
        <Typography scale='p3'>
          {formatMessage(
            vatIncluded ? pricesTaxInclMessage : pricesTaxNotInclMessage,
          )}
        </Typography>
        <Typography scale='p3'>
          {formatMessage(
            packShipIncluded
              ? serviceMessages.packagingAndShippingIncluded
              : serviceMessages.priceTablePackagingShippingNotIncluded,
          )}
        </Typography>
      </TableTitle>
      <TableBody data-testid={`tableBody${isSmUp ? 'Desktop' : 'Mobile'}`}>
        {!isSmUp &&
          mergedPrices.map((mergedPrice) => (
            <>
              <TableRow key={mergedPrice.title}>
                <TableCell colSpan={2} component='th' lightGrey scope='col'>
                  {mergedPrice.description ? (
                    <S.Blockchain
                      description={
                        <S.Desc color='charcoalGray' scale='p3'>
                          {mergedPrice.description}
                        </S.Desc>
                      }
                      title={
                        <Typography scale='h5' textAlign='left'>
                          {mergedPrice.title}
                        </Typography>
                      }
                    ></S.Blockchain>
                  ) : (
                    <S.Title scale='h5'>{mergedPrice.title}</S.Title>
                  )}
                </TableCell>
              </TableRow>
              {mergedPrice.items.map((item) => {
                const hasCategory2 = item.category2.some((str) => !!str);
                return hasCategory2 ? (
                  <>
                    <TableRow key={mergedPrice.title + item.category1}>
                      <TableCell align='center' colSpan={2}>
                        <S.Text scale='h6' textTransform='uppercase'>
                          {item.category1}
                        </S.Text>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      {renderCateogory2TableCell(item.category2)}
                      {renderPriceTableCell(item.price)}
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    {renderCateogory2TableCell([item.category1])}
                    {renderPriceTableCell(item.price)}
                  </TableRow>
                );
              })}
            </>
          ))}

        {isSmUp &&
          mergedPrices.map((mergedPrice) =>
            mergedPrice.items.map((item, index) => (
              <TableRow key={mergedPrice.title + item.category1}>
                {index === 0 && (
                  <TableCell
                    component='th'
                    rowSpan={mergedPrice.items.length || 1}
                    scope='row'
                    width={320}
                  >
                    {mergedPrice.description ? (
                      <S.Blockchain
                        description={
                          <S.Desc color='charcoalGray' scale='p3'>
                            {mergedPrice.description}
                          </S.Desc>
                        }
                        title={
                          <Typography scale='h5' textAlign='left'>
                            {mergedPrice.title}
                          </Typography>
                        }
                      ></S.Blockchain>
                    ) : (
                      <S.Title scale='h5'>{mergedPrice.title}</S.Title>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <S.Text
                    key={item.category1}
                    scale='h6'
                    textTransform='uppercase'
                  >
                    {item.category1}
                  </S.Text>
                </TableCell>
                {renderCateogory2TableCell(item.category2)}
                {renderPriceTableCell(item.price)}
              </TableRow>
            )),
          )}
      </TableBody>
    </Table>
  );
};

PriceTable.displayName = 'PriceTable';
